import { cn } from '@poseidon/components/cn';
import { Button, Tooltip } from '@poseidon/components/Input2';
import { General, Time } from '@poseidon/components/StrokeIcons';

import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { DEFAULT_WAIT_DAYS, STEP_META, stepLabel } from '~/components/Sequence/sequenceSteps';
import { WaitDurationInput, type WaitUnit } from '~/components/Sequence/WaitDurationInput';

interface Props {
  step: SequenceStep;
  /** Remove this chip from the group. Omit (the locked per-contact view) → no remove ✕. */
  onRemove?: () => void;
  /** Commit a new wait duration. Omit (the locked per-contact view) → the wait renders as a static pill. */
  onChangeWait?: (days: number, unit: WaitUnit) => void;
  /** Begin dragging this chip out of the group (wired by the editor). Omit → the chip isn't draggable. */
  onDragStart?: (e: React.PointerEvent) => void;
  /** Dim the chip while it's the one being dragged out. */
  dimmed?: boolean;
  /**
   * Open the content editor for this step (a non-draggable chip edits on body click; a draggable chip
   * edits via the parent's click/drag gesture). Omit → the step has nothing editable.
   */
  onEdit?: () => void;
  /** A validation problem with this step (e.g. missing body) — shows a red ring + reason tooltip. */
  invalid?: string | null;
  /** Locked view: freeze structure — static wait, no remove ✕ (also implied by omitting the callbacks). */
  readOnly?: boolean;
}

/**
 * One step inside a parallel container box (see {@link SequenceParallelArms}) — a removable, clickable
 * "chip". Clicking a chip opens its content editor (it's a step, like any spine node); an action step
 * is a small rounded icon tile; a wait step is a pill with its editable duration. A red ✕ fades in at
 * the corner on hover to remove it. When `onDragStart` is provided the chip can be dragged out onto the
 * spine (the editor turns it into a standalone step). Its delete sits in the corner (not to the right),
 * so chips pack into a tight horizontal row like a button group.
 *
 * Click-to-edit dispatch differs by draggability: a DRAGGABLE (group) chip's click is disambiguated
 * from a drag by the parent's threshold gesture ({@link SequenceGraphEditor} `onUp`), so it has no body
 * click here (that would also fire after a drag). A non-draggable (spawn) chip has no drag, so it edits
 * on a plain guarded body click.
 */
export function SequenceParallelChip({ step, onRemove, onChangeWait, onDragStart, dimmed, onEdit, invalid, readOnly = false }: Props) {
  const draggable = onDragStart != null;
  const beginDrag = (e: React.PointerEvent) => {
    // Let the delete button / wait input handle their own pointer events.
    if (e.target instanceof Element && e.target.closest('button, input, textarea, select, a')) return;
    if (onDragStart == null) return;
    e.stopPropagation();
    onDragStart(e);
  };
  const onBodyClick =
    !draggable && onEdit != null
      ? (e: React.MouseEvent) => {
          // Don't hijack clicks on the delete button / wait input.
          if (e.target instanceof Element && e.target.closest('button, input, textarea, select, a')) return;
          onEdit();
        }
      : undefined;

  return (
    <div
      onPointerDown={beginDrag}
      onClick={onBodyClick}
      className={cn(
        'group/chip relative inline-flex items-center',
        draggable && 'cursor-grab',
        onBodyClick != null && 'cursor-pointer',
        dimmed && 'pointer-events-none opacity-30',
      )}>
      {step.type !== 'wait' && (
        <Tooltip
          as="span"
          tooltip={invalid ?? stepLabel(step)}
          className={cn(
            'flex size-9 items-center justify-center rounded-lg border border-lynch-200 bg-white text-lynch-600 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-lynch-300 [&>svg]:size-5',
            invalid != null && 'ring-2 ring-red-400',
          )}>
          {STEP_META[step.type].icon}
        </Tooltip>
      )}
      {step.type === 'wait' && (
        <div className="flex h-9 items-center gap-1.5 rounded-full border border-lynch-200 bg-white pl-3 pr-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <Time.Clock className="size-4 shrink-0 text-lynch-500 dark:text-lynch-400" />
          {(readOnly || onChangeWait == null) && (
            <span className="whitespace-nowrap px-1 text-xs font-medium text-lynch-600 dark:text-lynch-300">{stepLabel(step)}</span>
          )}
          {!readOnly && onChangeWait != null && (
            <WaitDurationInput
              key={`${step.waitDays ?? DEFAULT_WAIT_DAYS}-${step.waitUnit ?? 'days'}`}
              days={step.waitDays ?? DEFAULT_WAIT_DAYS}
              unit={step.waitUnit ?? 'days'}
              onCommit={onChangeWait}
            />
          )}
        </div>
      )}
      {onRemove != null && (
        <Button
          size="2xs"
          color="red"
          tone="soft"
          aria-label="Remove step"
          tooltip="Remove step"
          icon={<General.XMark />}
          onClick={onRemove}
          confirm={{ title: 'Remove this step?', confirmLabel: 'Remove' }}
          className={cn(
            'absolute -right-1.5 -top-1.5',
            'pointer-events-none opacity-0 transition-opacity group-hover/chip:pointer-events-auto group-hover/chip:opacity-100',
          )}
        />
      )}
    </div>
  );
}
